/**
 * RTWO Blockchain Core Foundation — Consensus Readiness Assessment
 * RTWO v2.2 | Module ID: RTWO-BLCK-001
 */

import { isRuntimeGenesisFinalized } from '../../genesis-network-configuration/bootstrap/runtime-genesis-finalized-cache';
import { isConsensusRuntimeEnabled } from '../../consensus-runtime/configuration/consensus-runtime-config';
import { getCachedConnectedPeerCount } from '../../runtime/runtime-peer-connectivity-cache';
import { isBlockchainCoreEnabled } from '../configuration/blockchain-core-config';

export type BlockchainCoreConsensusReadinessLogEvent =
  | 'consensus_readiness_assessed'
  | 'consensus_readiness_enabled'
  | 'consensus_readiness_blocked';

const LOG_PREFIX = '[RTWO blockchain-core-consensus-readiness]';

export interface BlockchainCoreConsensusReadinessResult {
  consensusEnabled: boolean;
  genesisFinalized: boolean;
  genesisBlockHydrated: boolean;
  consensusRuntimeEnabled: boolean;
  connectedPeerCount: number;
  multiNodeRuntime: boolean;
  blockers: string[];
  source: string;
}

function logConsensusReadiness(
  level: 'info' | 'warn',
  event: BlockchainCoreConsensusReadinessLogEvent,
  message: string,
  context: Record<string, string | number | boolean | null> = {}
): void {
  const contextSuffix = Object.entries(context)
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => `${key}=${String(value)}`)
    .join(' ');

  const line = `${LOG_PREFIX} ${event} — ${message}${contextSuffix ? ` (${contextSuffix})` : ''}`;
  if (level === 'warn') {
    console.warn(line);
    return;
  }
  console.info(line);
}

function isMultiNodeRuntimeActive(): boolean {
  const multiNode = process.env.RTWO_MULTI_NODE_ENABLED?.trim().toLowerCase() === 'true';
  const runtimeMode = process.env.RTWO_RUNTIME_MODE?.trim().toLowerCase();
  return multiNode || runtimeMode === 'local-multi-node';
}

export function assessBlockchainCoreConsensusReadiness(input: {
  coreEnabled: boolean;
  backendConnected: boolean;
  blockCount: number;
}): BlockchainCoreConsensusReadinessResult {
  const blockers: string[] = [];
  const consensusRuntimeEnabled = isConsensusRuntimeEnabled();
  const multiNodeRuntime = isMultiNodeRuntimeActive();
  const connectedPeerCount = getCachedConnectedPeerCount();
  const genesisFinalized = isRuntimeGenesisFinalized();
  const genesisBlockHydrated = input.backendConnected && input.blockCount >= 1;

  if (!input.coreEnabled) {
    blockers.push('Blockchain core disabled.');
  }
  if (!input.backendConnected) {
    blockers.push('Blockchain core backend not connected.');
  }
  if (!genesisBlockHydrated) {
    blockers.push('Genesis block not hydrated in blockchain core.');
  }
  if (!genesisFinalized) {
    blockers.push('Genesis configuration is not finalized.');
  }
  if (!consensusRuntimeEnabled) {
    blockers.push('Consensus runtime disabled (RTWO_CONSENSUS_RUNTIME_ENABLED=false).');
  }
  if (multiNodeRuntime && connectedPeerCount === 0) {
    blockers.push('Multi-node runtime requires connected peers for consensus readiness.');
  }

  const consensusEnabled =
    input.coreEnabled &&
    input.backendConnected &&
    genesisBlockHydrated &&
    genesisFinalized &&
    consensusRuntimeEnabled &&
    (!multiNodeRuntime || connectedPeerCount > 0);

  logConsensusReadiness(
    consensusEnabled ? 'info' : 'warn',
    consensusEnabled ? 'consensus_readiness_enabled' : 'consensus_readiness_blocked',
    consensusEnabled
      ? 'Blockchain core consensus readiness enabled'
      : 'Blockchain core consensus readiness blocked',
    {
      genesisFinalized,
      genesisBlockHydrated,
      blockCount: input.blockCount,
      connectedPeerCount,
      multiNodeRuntime,
      blockerCount: blockers.length,
    }
  );

  logConsensusReadiness('info', 'consensus_readiness_assessed', 'Consensus readiness assessed', {
    consensusEnabled,
    genesisFinalized,
    blockCount: input.blockCount,
    connectedPeerCount,
  });

  return {
    consensusEnabled,
    genesisFinalized,
    genesisBlockHydrated,
    consensusRuntimeEnabled,
    connectedPeerCount,
    multiNodeRuntime,
    blockers,
    source: consensusEnabled
      ? 'Blockchain Core — Consensus Network Ready'
      : 'Blockchain Core — Consensus Readiness Blocked',
  };
}
